import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  Container,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import LoadingScreen from '../common/LoadingScreen';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const { login, isAuthenticated, authLoading } = useAuthContext();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      // Check if there's a stored redirect path
      const redirectPath = localStorage.getItem('redirectPath');
      const redirectAfterAuth = localStorage.getItem('redirectAfterAuth');

      // Priority: 1. redirectAfterAuth, 2. redirectPath, 3. default route
      if (redirectAfterAuth) {
        // Clean up the redirect flag
        localStorage.removeItem('redirectAfterAuth');
        // navigate(redirectAfterAuth);
      } else if (redirectPath) {
        // Clean up the stored path
        localStorage.removeItem('redirectPath');
        navigate(redirectPath);
      } else {
        // Default route
        navigate('/agents');
      }
      navigate('/agents');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // If still determining auth status, show loading screen
  if (authLoading) {
    return <LoadingScreen />;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    const isLoggedIn = login(username, password);

    // Login validation handled by the useEffect above
    if (!isLoggedIn) {
      setError('Invalid username or password');
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            align="center"
            color="primary"
          >
            KI-Agent Login
          </Typography>

          <Typography
            variant="body1"
            gutterBottom
            align="center"
            sx={{ mb: 3 }}
          >
            Log in to access the agent management system
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
            <TextField
              label="Password"
              variant="outlined"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
            >
              Log In
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
